import React from 'react'
import { isWidthUp } from '@material-ui/core/withWidth'
import { TileLayer, WMSTileLayer } from 'react-leaflet'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'

// Layer components
import PMTilesLayer from './PMTilesLayer'
import SigPACLayer from './SigPACLayer'
import RedNatura2000Layer from './RedNatura2000Layer'
import FaoGaulLayer, { FAO_GAUL_LEVELS } from './FaoGaulLayer'
import EcuadorPlantacionForestalLayer from './EcuadorPlantacionForestalLayer'
import EcuadorAccesibilidadLayer from './EcuadorAccesibilidadLayer'
import EcuadorConflictosUsoLayer from './EcuadorConflictosUsoLayer'
import EcuadorPobrezaRuralLayer from './EcuadorPobrezaRuralLayer'
import EcuadorZaeeCacaoLayer from './EcuadorZaeeCacaoLayer'
import EcuadorZaeeCafeLayer from './EcuadorZaeeCafeLayer'
import EcuadorZaeCacao25kLayer from './EcuadorZaeCacao25kLayer'
import EcuadorZaeCafeArabigoLayer from './EcuadorZaeCafeArabigoLayer'
import EcuadorZaeCafeRobustaLayer from './EcuadorZaeCafeRobustaLayer'
import PeruModalidadesAccesoForestalLayer, { SERFOR_MODALIDAD_ACCESO_LEGENDS } from './PeruModalidadesAccesoForestalLayer'
import PeruBosquesProduccionLayer from './PeruBosquesProduccionLayer'
import PeruEcosistemasFragilesLayer from './PeruEcosistemasFragilesLayer'
import PeruHabitatsCriticosLayer from './PeruHabitatsCriticosLayer'
import PeruAlertasTempranas2026Layer from './PeruAlertasTempranas2026Layer'
import PeruBosqueHumedo2025Layer from './PeruBosqueHumedo2025Layer'
import PeruPerdidaBosque2025Layer from './PeruPerdidaBosque2025Layer'
import GhlcLandCoverLayer, { GHLC_DEFAULT_MODE, GHLC_MODES, GHLC_PRODUCTS, ghlcLegend } from './GhlcLandCoverLayer'

// Blend-mode rules for GEE/GNW alert layers that ship opaque static tiles
import './geeAlertLayers.css'

// UI components
import LeafletLayersDrawer from './LeafletLayersDrawer'
import LeafletLayersControl from './LeafletLayersControl'

// Hooks
import { useHansenTileUrl, useJrcGfc2020TileUrl, useForTy2020TileUrl, useNaturalForest2020TileUrl, useGeeAlertLayers } from '../../adapters/earthengine'

const legendDem = L.control({ position: 'bottomleft' })
const DEMLEGENDSOURCE = 'https://servicios.idee.es/wmts/mdt/leyendas/Elevaciones.png'

// SIGPAC WMS tiles only render at high zoom levels
const SIGPAC_MIN_ZOOM = 16

// Layers whose source licence does not (yet) allow commercial use: the MAG Ecuador
// layers are CC BY-NC-SA, and UPRA (Frontera Agrícola), SERFOR and Geobosques publish
// no licence (ClickUp doc q7phu-7012 "Embedded map layers - license audit", 2026-10-09).
// They stay in the code so each one can be re-enabled when written permission
// arrives; until then they are neither listed in the layer drawer nor rendered,
// in app_frontend, embedded and public-map alike (all use this component).
export const LAYERS_PENDING_LICENSE = new Set([
  'Frontera Agricola Colombia',
  'Ecuador Plantacion Forestal 2025',
  'A centros de acopio de café/cacao (50m)',
  'Conflictos de Uso de las Tierras',
  'Pobreza Rural',
  'ZAEE Cacao (250k)',
  'ZAEE Café (250k)',
  'ZAE Cacao (25k)',
  'ZAE Café Arábigo (25k)',
  'ZAE Café Robusta (25k)',
  'Peru Modalidades de Acceso Forestal',
  'Peru Bosques de Producción Permanente',
  'Peru Ecosistemas Frágiles',
  'Peru Hábitats Críticos',
  'Peru Alertas Tempranas 2026',
  'Peru Bosque Húmedo 2025',
  'Peru Pérdida de Bosque 2001-2025',
])

// Backend `blend_mode` render hint -> CSS class on the Leaflet layer container
// (rules live in geeAlertLayers.css).
const GEE_BLEND_MODE_CLASSNAMES = {
  screen: 'gee-alert-layer-blend-screen',
}

export default function MapLayersManager({
  companyId,
  map,
  width,
  openDrawer = false,
  drawerWidth = 0,
  openAddParcel = { open: false },
  isPublic = false,
  noAppBar = false,
  onDemLegendVisibilityChange,
  controlTop = 76,
  controlRight = 12,
  forceSimpleControl = false,
  initialOverlayVisibility,
  hideOverlays = false,
}) {
  const isCompany400 = companyId === 400
  const { t } = useTranslation()

  // -- Base layer state --
  const [activeBaseLayer, setActiveBaseLayer] = React.useState('Base Layer')
  const [isDemLegendVisible, setIsDemLegendVisible] = React.useState(false)
  const dtmRef = React.useRef()

  // -- Overlay visibility state --
  const [overlayVisibility, setOverlayVisibility] = React.useState({
    'Tree Cover 2020 JRC v3': false,
    'Tree Cover 2020 JRC v4': false,
    'Forest Typology 2020 (ForTy)': false,
    'Natural Forest Probability 2020': false,
    'Protected Areas (Colombia)': false,
    'Protected Areas (Europe EEA)': false,
    'Natura 2000 (Europe)': false,
    'Indigenous Areas': false,
    'Fields of the World (2024-25)': false,
    'Frontera Agricola Colombia': false,
    'SIGPAC (Spain)': false,
    'Red Natura 2000 (Spain)': false,
    'Ecuador Plantacion Forestal 2025': false,
    'A centros de acopio de café/cacao (50m)': false,
    'Conflictos de Uso de las Tierras': false,
    'Pobreza Rural': false,
    'ZAEE Cacao (250k)': false,
    'ZAEE Café (250k)': false,
    'ZAE Cacao (25k)': false,
    'ZAE Café Arábigo (25k)': false,
    'ZAE Café Robusta (25k)': false,
    'Peru Modalidades de Acceso Forestal': false,
    'Peru Bosques de Producción Permanente': false,
    'Peru Ecosistemas Frágiles': false,
    'Peru Hábitats Críticos': false,
    'Peru Alertas Tempranas 2026': false,
    'Peru Bosque Húmedo 2025': false,
    'Peru Pérdida de Bosque 2001-2025': false,
    'Tree Cover Loss': false,
    'FAO Admin Boundaries (GAUL)': false,
    'Land Cover 30m (GHLC)': false,
    'Land Cover 10m (GHLC, 2020)': false,
    ...(initialOverlayVisibility || {}),
  })

  // -- Earth Engine tile hooks (company 400 only, fetched on layer toggle) --
  const { data: hansenData } = useHansenTileUrl(isPublic, isCompany400 && overlayVisibility['Tree Cover Loss'])
  const hansenTileUrl = hansenData?.tile_url
  const { data: jrcGfc2020Data } = useJrcGfc2020TileUrl(isPublic, isCompany400 && overlayVisibility['Tree Cover 2020 JRC v3'])
  const jrcGfc2020TileUrl = jrcGfc2020Data?.tile_url
  const { data: jrcGfc2020V4Data } = useJrcGfc2020TileUrl(isPublic, isCompany400 && overlayVisibility['Tree Cover 2020 JRC v4'], 4)
  const jrcGfc2020V4TileUrl = jrcGfc2020V4Data?.tile_url
  const { data: forTy2020Data } = useForTy2020TileUrl(isPublic, isCompany400 && overlayVisibility['Forest Typology 2020 (ForTy)'])
  const forTy2020TileUrl = forTy2020Data?.tile_url
  const { data: naturalForest2020Data } = useNaturalForest2020TileUrl(isPublic, isCompany400 && overlayVisibility['Natural Forest Probability 2020'])
  const naturalForest2020TileUrl = naturalForest2020Data?.tile_url

  // -- GEE alert state (company 400 only) --
  const [geeAlertVisibility, setGeeAlertVisibility] = React.useState({
    'DIST-ALERT': false,
    'GLAD-L after 2020': false,
    'GLAD-L before 2020': false,
    'GLAD-S2 after 2020': false,
    'GLAD-S2 before 2020': false,
    'Integrated Alerts (GFW)': false,
    'RADD after 2020': false,
    'RADD before 2020': false,
    'Water Stress (Aqueduct)': false,
    'ESG Risk (Aqueduct)': false,
    'Drivers of Deforestation Alerts': false,
    'Fire Detections (VIIRS)': false,
    'Tree Cover Loss from Fires': false,
    'Tree Cover Loss Drivers (1 km)': false,
  })
  const [overlayOpacity, setOverlayOpacity] = React.useState({})
  const [showSigpacZoomHint, setShowSigpacZoomHint] = React.useState(false)
  const [overlayYearRange, setOverlayYearRange] = React.useState({
    'Peru Pérdida de Bosque 2001-2025': [2001, 2025],
  })
  // Single-year layers: { [layerName]: year }.
  const [overlayYear, setOverlayYear] = React.useState({
    'Land Cover 30m (GHLC)': GHLC_PRODUCTS['30m'].defaultYear,
  })
  // Layers with display modes (GHLC: 'Default' = 10 classes, 'Detailed' = all classes).
  const [overlayMode, setOverlayMode] = React.useState({
    'Land Cover 30m (GHLC)': GHLC_DEFAULT_MODE,
    'Land Cover 10m (GHLC, 2020)': GHLC_DEFAULT_MODE,
  })
  const [geeAlertOpacity, setGeeAlertOpacity] = React.useState({})
  // Per-layer window in days for windowed alert layers (VIIRS fires):
  // { [layerName]: 7 | 30 | 90 }. Absent = 7 (backend default).
  const [geeAlertWindowDays, setGeeAlertWindowDays] = React.useState({})

  const geeAlertLayers = useGeeAlertLayers(isCompany400, geeAlertVisibility, isPublic, geeAlertWindowDays)

  // -- Helper to update DEM legend visibility and notify parent --
  const updateDemLegendVisible = (visible) => {
    setIsDemLegendVisible(visible)
    if (onDemLegendVisibilityChange) onDemLegendVisibilityChange(visible)
  }

  // -- Handlers --
  const handleBaseLayerChange = (layerName) => {
    setActiveBaseLayer(layerName)
    if (layerName === 'MDT Spain (5m)') {
      try {
        legendDem.onAdd = () => {
          const img = L.DomUtil.create('img', 'demLegend')
          img.src = DEMLEGENDSOURCE
          img.style.marginLeft = (openDrawer ? drawerWidth + 10 : '10') + 'px'
          return img
        }
        legendDem.addTo(map)
        updateDemLegendVisible(true)
        if (dtmRef.current) dtmRef.current.bringToBack()
      } catch (error) {
        console.error('Error adding DEM legend:', error)
      }
    } else {
      try {
        legendDem.remove()
        updateDemLegendVisible(false)
      } catch (error) {
        console.error('Error removing DEM legend:', error)
      }
    }
  }

  const handleOverlayToggle = (layerName, visible) => {
    if (LAYERS_PENDING_LICENSE.has(layerName)) return
    setOverlayVisibility(prev => ({ ...prev, [layerName]: visible }))
    if (layerName === 'SIGPAC (Spain)' && visible && map && map.getZoom() < SIGPAC_MIN_ZOOM) {
      setShowSigpacZoomHint(true)
    }
  }

  const handleGeeAlertToggle = (layerName, visible) => {
    setGeeAlertVisibility(prev => ({ ...prev, [layerName]: visible }))
  }

  const handleOverlayOpacityChange = (layerName, opacity) => {
    setOverlayOpacity(prev => ({ ...prev, [layerName]: opacity }))
  }

  const handleOverlayYearRangeChange = (layerName, yearRange) => {
    setOverlayYearRange(prev => ({ ...prev, [layerName]: yearRange }))
  }

  const handleOverlayYearChange = (layerName, year) => {
    setOverlayYear(prev => ({ ...prev, [layerName]: year }))
  }

  const handleOverlayModeChange = (layerName, mode) => {
    setOverlayMode(prev => ({ ...prev, [layerName]: mode }))
  }

  const handleGeeAlertOpacityChange = (layerName, opacity) => {
    setGeeAlertOpacity(prev => ({ ...prev, [layerName]: opacity }))
  }

  const handleGeeAlertWindowDaysChange = (layerName, days) => {
    setGeeAlertWindowDays(prev => ({ ...prev, [layerName]: days }))
  }

  const handleClearAllLayers = () => {
    setOverlayVisibility(prev => {
      const cleared = {}
      Object.keys(prev).forEach(k => { cleared[k] = false })
      return cleared
    })
    setGeeAlertVisibility(prev => {
      const cleared = {}
      Object.keys(prev).forEach(k => { cleared[k] = false })
      return cleared
    })
  }

  // A layer is rendered only when toggled on AND its licence allows it.
  const isShown = (layerName) => Boolean(overlayVisibility[layerName]) && !LAYERS_PENDING_LICENSE.has(layerName)

  // -- Overlay groups config for drawer/control --
  const sigpacOverlay = { name: 'SIGPAC (Spain)', visible: overlayVisibility['SIGPAC (Spain)'], opacity: overlayOpacity['SIGPAC (Spain)'] ?? 1, legend: [{ color: '#FFD700', label: 'Recintos SIGPAC' }] }
  // Protected Areas (Colombia): official RUNAP source, CC BY-SA 4.0.
  const protectedAreasColombiaOverlay = { name: 'Protected Areas (Colombia)', visible: overlayVisibility['Protected Areas (Colombia)'], noOpacity: true }

  const allOverlayGroups = [
    {
      name: 'Satellite Layers',
      accent: '#232C57',
      overlays: [
        { name: 'Tree Cover 2020 JRC v3', visible: overlayVisibility['Tree Cover 2020 JRC v3'], opacity: overlayOpacity['Tree Cover 2020 JRC v3'] ?? 0.8, legend: jrcGfc2020Data?.legend || [{ color: '#4D9221', label: 'Forest' }] },
        { name: 'Tree Cover 2020 JRC v4', visible: overlayVisibility['Tree Cover 2020 JRC v4'], opacity: overlayOpacity['Tree Cover 2020 JRC v4'] ?? 0.8, legend: jrcGfc2020V4Data?.legend || [{ color: '#00B894', label: 'Forest' }] },
        { name: 'Tree Cover Loss', visible: overlayVisibility['Tree Cover Loss'], opacity: overlayOpacity['Tree Cover Loss'] ?? 0.8 },
        { name: 'Forest Typology 2020 (ForTy)', visible: overlayVisibility['Forest Typology 2020 (ForTy)'], opacity: overlayOpacity['Forest Typology 2020 (ForTy)'] ?? 0.8, legend: (forTy2020Data?.legend || []).map(l => ({ color: l.color, label: l.label })) },
        { name: 'Natural Forest Probability 2020', visible: overlayVisibility['Natural Forest Probability 2020'], opacity: overlayOpacity['Natural Forest Probability 2020'] ?? 0.8, legend: naturalForest2020Data?.legend || [] },
      ]
    },
    {
      name: 'Global Dataset',
      accent: '#232C57',
      overlays: [
        { name: 'Indigenous Areas', visible: overlayVisibility['Indigenous Areas'], noOpacity: true },
        { name: 'Fields of the World (2024-25)', visible: overlayVisibility['Fields of the World (2024-25)'], noOpacity: true, legend: [{ color: '#FFD54F', label: 'Field boundaries' }] },
        { name: 'FAO Admin Boundaries (GAUL)', visible: overlayVisibility['FAO Admin Boundaries (GAUL)'], opacity: overlayOpacity['FAO Admin Boundaries (GAUL)'] ?? 0.9, legend: FAO_GAUL_LEVELS.map(l => ({ color: l.color, label: l.label })) },
        { name: 'Land Cover 30m (GHLC)', visible: overlayVisibility['Land Cover 30m (GHLC)'], opacity: overlayOpacity['Land Cover 30m (GHLC)'] ?? 0.7, years: GHLC_PRODUCTS['30m'].years, year: overlayYear['Land Cover 30m (GHLC)'], modes: GHLC_MODES, mode: overlayMode['Land Cover 30m (GHLC)'], legend: ghlcLegend('30m', overlayMode['Land Cover 30m (GHLC)']) },
        { name: 'Land Cover 10m (GHLC, 2020)', visible: overlayVisibility['Land Cover 10m (GHLC, 2020)'], opacity: overlayOpacity['Land Cover 10m (GHLC, 2020)'] ?? 0.7, modes: GHLC_MODES, mode: overlayMode['Land Cover 10m (GHLC, 2020)'], legend: ghlcLegend('10m', overlayMode['Land Cover 10m (GHLC, 2020)']) },
      ]
    },
    {
      name: 'Europe',
      accent: '#003399',
      overlays: [
        { name: 'Protected Areas (Europe EEA)', visible: overlayVisibility['Protected Areas (Europe EEA)'], noOpacity: true },
        { name: 'Natura 2000 (Europe)', visible: overlayVisibility['Natura 2000 (Europe)'], noOpacity: true },
      ]
    },
    {
      name: 'Spain',
      accent: '#C60B1E',
      overlays: [
        sigpacOverlay,
        { name: 'Red Natura 2000 (Spain)', visible: overlayVisibility['Red Natura 2000 (Spain)'], opacity: overlayOpacity['Red Natura 2000 (Spain)'] ?? 0.6, legendUrl: 'https://wms.mapama.gob.es/sig/Biodiversidad/RedNatura/wms.aspx?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.3.0&FORMAT=image/png&LAYER=PS.ProtectedSite' },
      ]
    },
    {
      name: 'Colombia',
      accent: '#FCD116',
      overlays: [
        protectedAreasColombiaOverlay,
        { name: 'Frontera Agricola Colombia', visible: overlayVisibility['Frontera Agricola Colombia'], noOpacity: true },
      ]
    },
    {
      name: 'Ecuador',
      accent: '#034EA2',
      overlays: [
        { name: 'Ecuador Plantacion Forestal 2025', visible: overlayVisibility['Ecuador Plantacion Forestal 2025'], opacity: overlayOpacity['Ecuador Plantacion Forestal 2025'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/cobertura/E5k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_fc001_plantacion_forestal_2025&FORMAT=image/png` },
        { name: 'A centros de acopio de café/cacao (50m)', visible: overlayVisibility['A centros de acopio de café/cacao (50m)'], opacity: overlayOpacity['A centros de acopio de café/cacao (50m)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/geosigtierras/accesibilidad/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=acc_cafe_cacao_reclas&FORMAT=image/png` },
        { name: 'Conflictos de Uso de las Tierras', visible: overlayVisibility['Conflictos de Uso de las Tierras'], opacity: overlayOpacity['Conflictos de Uso de las Tierras'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/fisiografia/E25k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_em000_conflictos_uso_tierras&FORMAT=image/png` },
        { name: 'Pobreza Rural', visible: overlayVisibility['Pobreza Rural'], opacity: overlayOpacity['Pobreza Rural'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/agroestadistica/tipologias_territorio_hih/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=TIPOLOGIA_MICRORREGIONES_AGRICOLA_PECUARIO_pobreza_rural&FORMAT=image/png` },
        { name: 'ZAEE Cacao (250k)', visible: overlayVisibility['ZAEE Cacao (250k)'], opacity: overlayOpacity['ZAEE Cacao (250k)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/demarcacion/E250k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_zaee_cacao&FORMAT=image/png` },
        { name: 'ZAEE Café (250k)', visible: overlayVisibility['ZAEE Café (250k)'], opacity: overlayOpacity['ZAEE Café (250k)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/demarcacion/E250k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_zaee_cafe&FORMAT=image/png` },
        { name: 'ZAE Cacao (25k)', visible: overlayVisibility['ZAE Cacao (25k)'], opacity: overlayOpacity['ZAE Cacao (25k)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/demarcacion/E25k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_hg000_zae_cacao&FORMAT=image/png` },
        { name: 'ZAE Café Arábigo (25k)', visible: overlayVisibility['ZAE Café Arábigo (25k)'], opacity: overlayOpacity['ZAE Café Arábigo (25k)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/demarcacion/E25k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_hg000_zae_cafe_arabigo&FORMAT=image/png` },
        { name: 'ZAE Café Robusta (25k)', visible: overlayVisibility['ZAE Café Robusta (25k)'], opacity: overlayOpacity['ZAE Café Robusta (25k)'] ?? 0.6, legendUrl: `https://g8a078u1g6.execute-api.eu-west-1.amazonaws.com/app/wms-ecuador/demarcacion/E25k/wms?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.1.1&LAYER=vw_hg000_zae_cafe_robusta&FORMAT=image/png` },
      ]
    },
    {
      name: 'Peru',
      accent: '#D91023',
      overlays: [
        { name: 'Peru Modalidades de Acceso Forestal', visible: overlayVisibility['Peru Modalidades de Acceso Forestal'], opacity: overlayOpacity['Peru Modalidades de Acceso Forestal'] ?? 0.6, legendUrls: SERFOR_MODALIDAD_ACCESO_LEGENDS },
        { name: 'Peru Bosques de Producción Permanente', visible: overlayVisibility['Peru Bosques de Producción Permanente'], opacity: overlayOpacity['Peru Bosques de Producción Permanente'] ?? 0.6, legend: [{ color: '#CBF5E3', label: 'Bosques de Producción Permanente' }] },
        { name: 'Peru Ecosistemas Frágiles', visible: overlayVisibility['Peru Ecosistemas Frágiles'], opacity: overlayOpacity['Peru Ecosistemas Frágiles'] ?? 0.6, legend: [{ color: '#CFFAC9', label: 'Ecosistemas Frágiles' }] },
        { name: 'Peru Hábitats Críticos', visible: overlayVisibility['Peru Hábitats Críticos'], opacity: overlayOpacity['Peru Hábitats Críticos'] ?? 0.6, legend: [{ color: '#C8E4F2', label: 'Hábitats Críticos' }] },
        { name: 'Peru Alertas Tempranas 2026', visible: overlayVisibility['Peru Alertas Tempranas 2026'], opacity: overlayOpacity['Peru Alertas Tempranas 2026'] ?? 0.6, legend: [{ color: '#ED160A', label: 'Alertas tempranas 2026' }] },
        { name: 'Peru Bosque Húmedo 2025', visible: overlayVisibility['Peru Bosque Húmedo 2025'], opacity: overlayOpacity['Peru Bosque Húmedo 2025'] ?? 0.6, legend: [{ color: '#A1C562', label: 'Bosque al 2025' }, { color: '#CCCCCC', label: 'No bosque al 2000' }, { color: '#FFAA00', label: 'Pérdida antrópica 2025' }, { color: '#00C5FF', label: 'Pérdida natural 2025' }, { color: '#ED160A', label: 'Pérdida 2001-2024' }, { color: '#0066FF', label: 'Hidrografía' }] },
        { name: 'Peru Pérdida de Bosque 2001-2025', visible: overlayVisibility['Peru Pérdida de Bosque 2001-2025'], opacity: overlayOpacity['Peru Pérdida de Bosque 2001-2025'] ?? 0.6, yearRange: overlayYearRange['Peru Pérdida de Bosque 2001-2025'] ?? [2001, 2025], legendGradient: true, legend: [{ color: '#FFAA00', label: 'Pérdida antrópica 2025' }, { color: '#00C5FF', label: 'Pérdida natural 2025' }] },
      ]
    },
  ]

  // Drop the licence-pending layers from the drawer, and any group left empty.
  const overlayGroups = allOverlayGroups
    .map(group => ({ ...group, overlays: group.overlays.filter(o => !LAYERS_PENDING_LICENSE.has(o.name)) }))
    .filter(group => group.overlays.length > 0)

  const baseLayers = [
    { name: 'Base Layer', checked: activeBaseLayer === 'Base Layer' },
    { name: 'MDT Spain (5m)', checked: activeBaseLayer === 'MDT Spain (5m)' },
  ]

  // Layers available to every company from the simple control: SIGPAC only.
  const basicGroups = [
    { name: 'Spain', accent: '#C60B1E', overlays: [sigpacOverlay] },
  ]

  // Overlays (everything that isn't a Base Layer) are restricted to company 400,
  // except SIGPAC which every company can toggle from the simple control.
  if (!isWidthUp('sm', width)) return null

  return (
    <>
      {/* -- Base Layers -- */}
      {activeBaseLayer === 'Base Layer' && (
        <TileLayer
          attribution="Maps Hybrid"
          maxZoom={24}
          maxNativeZoom={20}
          url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
        />
      )}
      {activeBaseLayer === 'MDT Spain (5m)' && (
        <WMSTileLayer
          ref={dtmRef}
          layers="EL.ElevationGridCoverage,EL.ContourLine"
          maxZoom={28}
          format="image/png"
          url="https://servicios.idee.es/wms-inspire/mdt"
        />
      )}

      {/* -- Data overlays (everything below is NOT a base layer): company 400 only.
            Base layers above stay available to all companies. -- */}
      {isCompany400 && (<>
      {/* -- Satellite Overlay Layers -- */}
      {jrcGfc2020TileUrl && overlayVisibility['Tree Cover 2020 JRC v3'] && (
        <TileLayer
          url={jrcGfc2020TileUrl}
          attribution="&copy; European Commission - JRC (via Google Earth Engine)"
          maxZoom={24}
          opacity={overlayOpacity['Tree Cover 2020 JRC v3'] ?? 0.8}
        />
      )}
      {jrcGfc2020V4TileUrl && overlayVisibility['Tree Cover 2020 JRC v4'] && (
        <TileLayer
          url={jrcGfc2020V4TileUrl}
          attribution="&copy; European Commission - JRC GFC2020 v4 (via Google Earth Engine)"
          maxZoom={24}
          opacity={overlayOpacity['Tree Cover 2020 JRC v4'] ?? 0.8}
        />
      )}
      {hansenTileUrl && overlayVisibility['Tree Cover Loss'] && (
        <TileLayer
          url={hansenTileUrl}
          attribution="&copy; Hansen/UMD/Google/USGS/NASA"
          maxZoom={24}
          opacity={overlayOpacity['Tree Cover Loss'] ?? 0.8}
        />
      )}
      {forTy2020TileUrl && overlayVisibility['Forest Typology 2020 (ForTy)'] && (
        <TileLayer
          url={forTy2020TileUrl}
          attribution="&copy; Google DeepMind / Nature Trace (CC BY 4.0, via Google Earth Engine)"
          maxZoom={24}
          opacity={overlayOpacity['Forest Typology 2020 (ForTy)'] ?? 0.8}
        />
      )}
      {naturalForest2020TileUrl && overlayVisibility['Natural Forest Probability 2020'] && (
        <TileLayer
          url={naturalForest2020TileUrl}
          attribution="&copy; Google (CC BY 4.0, via Google Earth Engine)"
          maxZoom={24}
          opacity={overlayOpacity['Natural Forest Probability 2020'] ?? 0.8}
        />
      )}

      {/* -- Global Dataset Layers -- */}
      {/* Protected Areas (Colombia): official RUNAP source, CC BY-SA 4.0
          (attribution in the layer tooltip and in the PMTiles metadata). */}
      {overlayVisibility['Protected Areas (Colombia)'] && (
        <PMTilesLayer key="protected-areas-co-layer" layerName="protected-areas-co" layerId="protected-areas-co" displayName="Protected Areas (Colombia)" isPublic={isPublic} style={{ color: "#4DAF4A", fillColor: "#4DAF4A", fillOpacity: 0.3, weight: 2, opacity: 0.8 }} />
      )}
      {/* European protected areas, both CC BY 4.0: EEA NatDA (national
          designations) + EU Natura 2000 (directives network) — together they
          replace the WDPA for the 37 public-dataset Eionet countries. */}
      {overlayVisibility['Protected Areas (Europe EEA)'] && (
        <PMTilesLayer key="protected-areas-eu-layer" layerName="protected-areas-eu" layerId="protected-areas-eu" displayName="Protected Areas (Europe EEA)" isPublic={isPublic} style={{ color: "#4DAF4A", fillColor: "#4DAF4A", fillOpacity: 0.3, weight: 2, opacity: 0.8 }} />
      )}
      {overlayVisibility['Natura 2000 (Europe)'] && (
        <PMTilesLayer key="natura2000-eu-layer" layerName="natura2000-eu" layerId="natura2000-eu" displayName="Natura 2000 (Europe)" isPublic={isPublic} style={{ color: "#2A9D8F", fillColor: "#2A9D8F", fillOpacity: 0.3, weight: 2, opacity: 0.8 }} />
      )}
      {overlayVisibility['Indigenous Areas'] && (
        <PMTilesLayer key="indigenous-areas-layer" layerName="indigenous-areas" layerId="indigenous-areas" displayName="Indigenous Areas" isPublic={isPublic} style={{ color: "#3A5D63", fillColor: "#3A5D63", fillOpacity: 0.3, weight: 2, opacity: 0.8 }} />
      )}
      {overlayVisibility['Fields of the World (2024-25)'] && (
        <PMTilesLayer key="fields-of-the-world-layer" layerName="fields-of-the-world" layerId="fields-of-the-world" displayName="Fields of the World" isPublic={isPublic} style={{ color: "#FFD54F", fillColor: "#FFD54F", fillOpacity: 0.1, weight: 1, opacity: 0.9 }} />
      )}
      {isShown('Frontera Agricola Colombia') && (
        <PMTilesLayer key="frontera-agricola-colombia-layer" layerName="frontera-agricola-colombia" layerId="frontera-agricola-colombia" displayName="Frontera Agricola Colombia" style={{ color: "#8B4513", fillColor: "#8B4513", fillOpacity: 0.3, weight: 2, opacity: 0.8 }} />
      )}

      {/* -- Country-specific WMS Layers -- */}
      {overlayVisibility['Red Natura 2000 (Spain)'] && <RedNatura2000Layer opacity={overlayOpacity['Red Natura 2000 (Spain)'] ?? 0.6} />}
      {isCompany400 && overlayVisibility['FAO Admin Boundaries (GAUL)'] && <FaoGaulLayer opacity={overlayOpacity['FAO Admin Boundaries (GAUL)'] ?? 0.9} />}
      {/* GHLC land cover 30 m / 10 m (OpenGeoHub / IFAD CAPEO, CC BY-SA 4.0): company 400 only. */}
      {isCompany400 && overlayVisibility['Land Cover 30m (GHLC)'] && <GhlcLandCoverLayer product="30m" opacity={overlayOpacity['Land Cover 30m (GHLC)'] ?? 0.7} year={overlayYear['Land Cover 30m (GHLC)']} mode={overlayMode['Land Cover 30m (GHLC)']} />}
      {isCompany400 && overlayVisibility['Land Cover 10m (GHLC, 2020)'] && <GhlcLandCoverLayer product="10m" opacity={overlayOpacity['Land Cover 10m (GHLC, 2020)'] ?? 0.7} year={2020} mode={overlayMode['Land Cover 10m (GHLC, 2020)']} />}
      {isShown('Ecuador Plantacion Forestal 2025') && <EcuadorPlantacionForestalLayer opacity={overlayOpacity['Ecuador Plantacion Forestal 2025'] ?? 0.6} />}
      {isShown('A centros de acopio de café/cacao (50m)') && <EcuadorAccesibilidadLayer opacity={overlayOpacity['A centros de acopio de café/cacao (50m)'] ?? 0.6} />}
      {isShown('Conflictos de Uso de las Tierras') && <EcuadorConflictosUsoLayer opacity={overlayOpacity['Conflictos de Uso de las Tierras'] ?? 0.6} />}
      {isShown('Pobreza Rural') && <EcuadorPobrezaRuralLayer opacity={overlayOpacity['Pobreza Rural'] ?? 0.6} />}
      {isShown('ZAEE Cacao (250k)') && <EcuadorZaeeCacaoLayer opacity={overlayOpacity['ZAEE Cacao (250k)'] ?? 0.6} />}
      {isShown('ZAEE Café (250k)') && <EcuadorZaeeCafeLayer opacity={overlayOpacity['ZAEE Café (250k)'] ?? 0.6} />}
      {isShown('ZAE Cacao (25k)') && <EcuadorZaeCacao25kLayer opacity={overlayOpacity['ZAE Cacao (25k)'] ?? 0.6} />}
      {isShown('ZAE Café Arábigo (25k)') && <EcuadorZaeCafeArabigoLayer opacity={overlayOpacity['ZAE Café Arábigo (25k)'] ?? 0.6} />}
      {isShown('ZAE Café Robusta (25k)') && <EcuadorZaeCafeRobustaLayer opacity={overlayOpacity['ZAE Café Robusta (25k)'] ?? 0.6} />}
      {isShown('Peru Modalidades de Acceso Forestal') && <PeruModalidadesAccesoForestalLayer opacity={overlayOpacity['Peru Modalidades de Acceso Forestal'] ?? 0.6} />}
      {isShown('Peru Bosques de Producción Permanente') && <PeruBosquesProduccionLayer opacity={overlayOpacity['Peru Bosques de Producción Permanente'] ?? 0.6} />}
      {isShown('Peru Ecosistemas Frágiles') && <PeruEcosistemasFragilesLayer opacity={overlayOpacity['Peru Ecosistemas Frágiles'] ?? 0.6} />}
      {isShown('Peru Hábitats Críticos') && <PeruHabitatsCriticosLayer opacity={overlayOpacity['Peru Hábitats Críticos'] ?? 0.6} />}
      {isShown('Peru Alertas Tempranas 2026') && <PeruAlertasTempranas2026Layer opacity={overlayOpacity['Peru Alertas Tempranas 2026'] ?? 0.6} />}
      {isShown('Peru Bosque Húmedo 2025') && <PeruBosqueHumedo2025Layer opacity={overlayOpacity['Peru Bosque Húmedo 2025'] ?? 0.6} />}
      {isShown('Peru Pérdida de Bosque 2001-2025') && <PeruPerdidaBosque2025Layer opacity={overlayOpacity['Peru Pérdida de Bosque 2001-2025'] ?? 0.6} yearRange={overlayYearRange['Peru Pérdida de Bosque 2001-2025'] ?? [2001, 2025]} />}

      {/* -- GEE Alert Layers (company 400) -- */}
      {isCompany400 && Object.entries(geeAlertLayers).map(([name, query]) => {
        if (!geeAlertVisibility[name] || !query.data?.tile_url) return null
        const { attribution, blend_mode: blendMode, max_native_zoom: maxNativeZoom } = query.data
        // Static tile caches (e.g. the WUR drivers coverage mask) are opaque
        // pink/black PNGs meant for a client-side decoder. Compositing the
        // layer with mix-blend-mode makes the black transparent without any
        // pixel work; the class is applied to the Leaflet layer container.
        const className = GEE_BLEND_MODE_CLASSNAMES[blendMode]
        return (
          <TileLayer
            key={`gee-${name}`}
            url={query.data.tile_url}
            maxZoom={24}
            {...(maxNativeZoom ? { maxNativeZoom } : {})}
            {...(className ? { className } : {})}
            {...(attribution ? { attribution } : {})}
            opacity={geeAlertOpacity[name] ?? 0.8}
          />
        )
      })}
      </>)}

      {/* SIGPAC recinto — available to all companies (mainly during add-parcel flow) */}
      {(openAddParcel.open || overlayVisibility['SIGPAC (Spain)']) && (
        <SigPACLayer opacity={overlayOpacity['SIGPAC (Spain)'] ?? 1} />
      )}

      <Snackbar
        open={showSigpacZoomHint}
        autoHideDuration={6000}
        onClose={() => setShowSigpacZoomHint(false)}
      >
        <Alert severity="info" onClose={() => setShowSigpacZoomHint(false)}>
          {t('Zoom in to see the SIGPAC layer')}
        </Alert>
      </Snackbar>

      {/* -- Layer Control UI -- */}
      {isCompany400 && !forceSimpleControl && !hideOverlays ? (
        <LeafletLayersDrawer
          baseLayers={baseLayers}
          overlayGroups={overlayGroups}
          onBaseLayerChange={handleBaseLayerChange}
          onOverlayToggle={handleOverlayToggle}
          onOverlayOpacityChange={handleOverlayOpacityChange}
          onOverlayYearRangeChange={handleOverlayYearRangeChange}
          onOverlayYearChange={handleOverlayYearChange}
          onOverlayModeChange={handleOverlayModeChange}
          onClearAllLayers={handleClearAllLayers}
          geeAlertLayers={geeAlertLayers}
          geeAlertVisibility={geeAlertVisibility}
          geeAlertOpacity={geeAlertOpacity}
          onGeeAlertToggle={handleGeeAlertToggle}
          onGeeAlertOpacityChange={handleGeeAlertOpacityChange}
          geeAlertWindowDays={geeAlertWindowDays}
          onGeeAlertWindowDaysChange={handleGeeAlertWindowDaysChange}
          noAppBar={noAppBar}
          top={controlTop}
          right={controlRight}
        />
      ) : (
        <LeafletLayersControl
          baseLayers={baseLayers}
          overlayGroups={
            hideOverlays ? [] : (isCompany400 ? overlayGroups : basicGroups)
          }
          onBaseLayerChange={handleBaseLayerChange}
          onOverlayToggle={handleOverlayToggle}
          top={controlTop}
          right={controlRight}
        />
      )}
    </>
  )
}
